import { signal } from "@preact/signals-react";

import { hasConfiguredBackend } from "@/shared/api/backend";
import { sessionUser } from "@/pages/authentication/state/session";
import { onSyncClose, onSyncMessage, publishSyncMessage, syncConnectionState, type SyncEnvelope } from "@/shared/api/sync";

export type PresenceUser = {
    client_id?: string;
    user_id: string;
    user_name?: string;
    user_role?: string;
    last_active_at?: string;
    available?: boolean;
    availability_start?: string;
    availability_end?: string;
};

export const presenceUsers = signal<PresenceUser[]>([]);
export const presenceClock = signal(Date.now());
export const presenceAvailabilityPending = signal<boolean | null>(null);
export const presenceAvailabilityError = signal("");

const presenceClockIntervalMS = 10_000;
const presenceIdleThresholdMS = 60_000;
const presenceActivityThrottleMS = 10_000;

let initialized = false;
let presenceClockTimer: number | null = null;
let presenceActivityTimer: number | null = null;
let lastPresenceActivityAt = 0;
let pendingWindow: { availability_start: string; availability_end: string } | null = null;

export function initPresenceSync() {
    if (initialized) {
        return;
    }

    initialized = true;
    onSyncMessage(handleSyncMessage);
    onSyncClose(handleSyncClose);
    attachActivityListeners();
    startPresenceClock();
}

export function resetPresenceState() {
    presenceUsers.value = [];
    presenceAvailabilityPending.value = null;
    pendingWindow = null;
    presenceAvailabilityError.value = "";
    presenceClock.value = Date.now();
    lastPresenceActivityAt = 0;
    if (presenceActivityTimer !== null) {
        window.clearTimeout(presenceActivityTimer);
        presenceActivityTimer = null;
    }
}

function handleSyncMessage(message: SyncEnvelope) {
    if (!sessionUser.value) {
        return;
    }

    switch (message.type) {
        case "presence.sync": {
            const payload = message.data as { users?: PresenceUser[] } | undefined;
            setPresenceUsers(payload?.users ?? []);
            presenceAvailabilityPending.value = null;
            break;
        }
        case "presence.joined":
        case "presence.updated": {
            const payload = message.data as PresenceUser | undefined;
            if (payload?.user_id) {
                upsertPresenceUser(payload);
                const windowMatches = pendingWindow && Date.parse(payload.availability_start || "") === Date.parse(pendingWindow.availability_start) && Date.parse(payload.availability_end || "") === Date.parse(pendingWindow.availability_end);
                if (payload.user_id === sessionUser.value?.id && (pendingWindow ? windowMatches : payload.available === presenceAvailabilityPending.value)) {
                    presenceAvailabilityPending.value = null;
                    pendingWindow = null;
                    presenceAvailabilityError.value = "";
                }
            }
            break;
        }
        case "presence.left": {
            const payload = message.data as PresenceUser | undefined;
            if (payload?.user_id) {
                removePresenceUser(payload.user_id, payload.client_id);
            }
            break;
        }
        case "error": {
            const payload = message.data as { code?: string; message?: string } | undefined;
            if (payload?.code === "availability_failed") {
                presenceAvailabilityPending.value = null;
                pendingWindow = null;
                presenceAvailabilityError.value = payload.message || "Unable to save availability.";
            }
            break;
        }
    }
}

function handleSyncClose() {
    if (presenceAvailabilityPending.value !== null) {
        presenceAvailabilityPending.value = null;
        presenceAvailabilityError.value = "Connection lost. Availability was not confirmed.";
    }
}

function setPresenceUsers(users: PresenceUser[]) {
    const next: PresenceUser[] = [];
    const seen = new Set<string>();

    for (const user of users) {
        if (!user.user_id) {
            continue;
        }

        const key = user.client_id || user.user_id;
        if (seen.has(key)) {
            continue;
        }

        seen.add(key);
        next.push(user);
    }

    presenceUsers.value = next;
}

function upsertPresenceUser(user: PresenceUser) {
    if (!user.user_id) {
        return;
    }

    const key = user.client_id || user.user_id;
    presenceUsers.value = (() => {
        const next = [...presenceUsers.value];
        const index = next.findIndex((entry) => (entry.client_id || entry.user_id) === key);
        if (index === -1) {
            return [...next, user];
        }

        next[index] = user;
        return next;
    })();
}

function removePresenceUser(userID: string, clientID?: string) {
    presenceUsers.value = presenceUsers.value.filter((entry) => {
        if (clientID) {
            return (entry.client_id || "") !== clientID;
        }
        return entry.user_id !== userID;
    });
}

function attachActivityListeners() {
    const notifyPresenceActivity = () => {
        if (!sessionUser.value || !hasConfiguredBackend()) {
            return;
        }

        const now = Date.now();
        const elapsed = now - lastPresenceActivityAt;
        if (elapsed >= presenceActivityThrottleMS) {
            if (publishSyncMessage("presence.activity")) {
                lastPresenceActivityAt = now;
            }
            return;
        }

        if (presenceActivityTimer !== null) {
            return;
        }

        presenceActivityTimer = window.setTimeout(() => {
            presenceActivityTimer = null;
            if (!sessionUser.value || !hasConfiguredBackend()) {
                return;
            }

            if (publishSyncMessage("presence.activity")) {
                lastPresenceActivityAt = Date.now();
            }
        }, presenceActivityThrottleMS - elapsed);
    };

    const handleVisibleActivity = () => {
        if (document.visibilityState === "visible") {
            notifyPresenceActivity();
        }
    };

    window.addEventListener("pointerdown", notifyPresenceActivity, { passive: true });
    window.addEventListener("pointermove", notifyPresenceActivity, { passive: true });
    window.addEventListener("keydown", notifyPresenceActivity);
    window.addEventListener("touchstart", notifyPresenceActivity, { passive: true });
    window.addEventListener("focus", notifyPresenceActivity);
    document.addEventListener("visibilitychange", handleVisibleActivity);
}

function startPresenceClock() {
    if (presenceClockTimer !== null) {
        return;
    }

    presenceClockTimer = window.setInterval(() => {
        presenceClock.value = Date.now();
    }, presenceClockIntervalMS);
}

export function isPresenceUserActive(user: PresenceUser, now = presenceClock.value) {
    const lastActiveAt = Date.parse(user.last_active_at || "");
    if (Number.isNaN(lastActiveAt)) {
        return false;
    }

    return now - lastActiveAt <= presenceIdleThresholdMS;
}

export function kickPresenceClient(clientID: string) {
    if (!clientID) {
        return false;
    }

    return publishSyncMessage("presence.kick", { client_id: clientID });
}

export function setPresenceAvailability(available: boolean, window?: { availability_start: string; availability_end: string }) {
    if (!sessionUser.value || syncConnectionState.value !== "connected" || presenceAvailabilityPending.value !== null) {
        return false;
    }

    presenceAvailabilityPending.value = available;
    pendingWindow = window ?? null;
    presenceAvailabilityError.value = "";
    const sent = publishSyncMessage("presence.availability", { available, ...window });
    if (sent) {
        return true;
    }

    presenceAvailabilityPending.value = null;
    presenceAvailabilityError.value = "Connection lost. Availability was not changed.";
    return false;
}
